import type { Product, ShopContent, ShopMedia, ShopReview, StockStatus as ShopStock } from '../../../types';
import type { CardProduct, Review, StockStatus, StorySection } from './types';
import type { MediaItem } from './format';

/**
 * Kopplingen mellan butikens data (Supabase) och komponenterna i den här
 * mappen. Komponenterna är Jonathans; bara den här filen känner till båda.
 */

export const toStockStatus = (s: ShopStock | undefined): StockStatus | undefined =>
  s === 'out' ? 'out_of_stock' : s;

/** Bara publicerade recensioner kommer hit (filtrera innan). */
export const toReview = (r: ShopReview): Review => ({
  id: r.id,
  name: r.name,
  rating: r.rating,
  text: r.text,
  avatarUrl: r.avatarUrl ?? null,
  media: (r.media || []).map((m) => ({ url: m.url, type: m.kind })),
  createdAt: r.createdAt ?? null,
  featured: r.featured,
});

/** Kundbilder utan recension, med bildtexten som namn. */
export const toMediaItems = (media: ShopMedia[]): MediaItem[] =>
  media.map((m) => ({ key: `fri:${m.id}`, name: m.caption || '', media: { url: m.url, type: m.kind } }));

/** Fälten som butiken räknar fram per produkt (StoreFront): paketets besparing, raden under namnet och lagerstatus. */
export type ShopCardFields = Product & {
  bundleSavings?: number;
  bundleImages?: string[];
  shopSubtitle?: string;
  shopStock?: ShopStock;
};

export const toCardProduct = (p: ShopCardFields, href: string): CardProduct => {
  const prices = p.variants && p.variants.length > 0 ? p.variants.map((v) => v.price) : [p.price];
  const price = Math.min(...prices);
  const savings = p.bundleSavings && p.bundleSavings > 0 ? p.bundleSavings : 0;
  return {
    id: p.id,
    name: p.name,
    subtitle: p.shopSubtitle ?? p.subtitle ?? null,
    price,
    priceFrom: new Set(prices).size > 1,
    compareAtPrice: savings > 0 ? Math.round((price + savings) * 100) / 100 : null,
    imageUrl: p.images?.[0] || p.imageUrl || p.bundleImages?.[0] || null,
    href,
    stockStatus: toStockStatus(p.shopStock || p.stockStatus),
    tag: p.seasonTag || null,
  };
};

/** Kaffet (grönt) och Om oss (rosa). Sektioner utan text visas inte. */
export const toStories = (content: Pick<ShopContent, 'coffee' | 'about'>): StorySection[] =>
  ([['kaffet', content.coffee, 'green'], ['om-oss', content.about, 'pink']] as const)
    .filter(([, s]) => s.title.trim() && s.text.trim())
    .map(([id, s, tone]) => ({ id, title: s.title, text: s.text, imageUrl: s.imageUrl ?? null, chips: s.facts, tone }));
